import { useParams, useNavigate, Link } from "react-router-dom";
import {
  useGetStudentByIdQuery,
  useUpdateStudentMutation,
  type Student,
} from "@/redux/slices/studentApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  School,
  MapPin,
  Calendar,
  CheckCircle2,
  XCircle,
  MessagesSquare,
  BookOpen,
  ShoppingBag,
  KeyRound,
  ShieldCheck,
  AlertCircle,
  Loader2,
  ExternalLink,
  Edit2,
  RefreshCw,
  Copy,
  Check
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useState } from "react";

export default function StudentDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(false);

  const { data: studentRes, isLoading, isError, refetch } = useGetStudentByIdQuery(id as string, {
    skip: !id,
  });

  const [updateStudent, { isLoading: isUpdating }] = useUpdateStudentMutation();

  const student = studentRes?.data;

  const handleToggleStatus = async () => {
    if (!student) return;
    const nextStatus = student.status === "active" ? "inactive" : "active";
    try {
      await updateStudent({
        id: student.id,
        body: { status: nextStatus },
      }).unwrap();
      toast.success(`Student status updated to ${nextStatus}!`);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update status");
    }
  };

  const handleToggleSupportKey = async () => {
    if (!student) return;
    try {
      await updateStudent({
        id: student.id,
        body: { supportKey: !student.supportKey },
      }).unwrap();
      toast.success(
        student.supportKey
          ? "Support key access disabled"
          : "Support key access enabled for student"
      );
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update support key");
    }
  };

  const handleCopyId = () => {
    if (!student?.id) return;
    navigator.clipboard.writeText(student.id);
    setCopiedId(true);
    toast.success("Account UUID copied to clipboard!");
    setTimeout(() => setCopiedId(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center space-y-3">
        <Loader2 className="h-9 w-9 animate-spin text-primary" />
        <p className="text-xs text-muted-foreground font-semibold">Loading student profile...</p>
      </div>
    );
  }

  if (isError || !student) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center space-y-4 max-w-md mx-auto text-center px-4">
        <div className="h-16 w-16 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center">
          <AlertCircle className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-foreground">Student Not Found</h2>
        <p className="text-xs text-muted-foreground">
          The requested student record does not exist or you do not have permission to access it.
        </p>
        <Button onClick={() => navigate(-1)} variant="outline" className="rounded-xl font-bold text-xs">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const initials = student.name
    ? student.name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "ST";

  return (
    <div className="min-h-screen bg-muted/20 pb-16 w-full max-w-full overflow-x-hidden">
      {/* Top Navigation Banner */}
      <div className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-20 w-full max-w-full">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="sm"
              className="rounded-xl h-9 px-3 gap-1.5 text-xs font-semibold shrink-0"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-sm sm:text-base font-bold text-foreground truncate max-w-[150px] sm:max-w-none">
                  {student.name}
                </h1>
                <Badge
                  variant={student.status === "active" ? "default" : "secondary"}
                  className={cn(
                    "rounded-md text-[10px] font-bold uppercase shrink-0",
                    student.status === "active"
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {student.status}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{student.mobile || student.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="rounded-xl h-9 px-3 text-xs font-semibold"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button
              onClick={handleToggleStatus}
              disabled={isUpdating}
              variant={student.status === "active" ? "outline" : "default"}
              size="sm"
              className={cn(
                "rounded-xl h-9 px-3 text-xs font-bold",
                student.status === "active" ? "hover:border-destructive/40 hover:text-destructive" : ""
              )}
            >
              {student.status === "active" ? (
                <>
                  <XCircle className="mr-1.5 h-3.5 w-3.5 text-destructive" /> Deactivate
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> Activate
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 sm:pt-6 space-y-4 sm:space-y-6 w-full max-w-full">
        {/* Profile Card Header */}
        <Card className="rounded-2xl border shadow-xs overflow-hidden bg-card w-full">
          <div className="p-4 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="h-14 w-14 sm:h-18 sm:w-18 rounded-2xl bg-gradient-to-br from-primary to-primary-hover text-primary-foreground flex items-center justify-center font-black text-xl sm:text-2xl shadow-md shrink-0">
                {initials}
              </div>
              <div className="space-y-1 min-w-0">
                <h2 className="text-lg sm:text-2xl font-black text-foreground truncate">{student.name}</h2>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {student.email && (
                    <a
                      href={`mailto:${student.email}`}
                      className="flex items-center gap-1.5 hover:text-primary transition-colors truncate"
                    >
                      <Mail className="h-3.5 w-3.5 text-primary shrink-0" /> <span className="truncate">{student.email}</span>
                    </a>
                  )}
                  {student.mobile && (
                    <a
                      href={`tel:${student.mobile}`}
                      className="flex items-center gap-1.5 hover:text-primary transition-colors shrink-0"
                    >
                      <Phone className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> {student.mobile}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button
                type="button"
                variant={student.supportKey ? "default" : "outline"}
                size="sm"
                onClick={handleToggleSupportKey}
                disabled={isUpdating}
                className="rounded-xl h-9 text-xs font-bold gap-1.5 w-full sm:w-auto"
              >
                <KeyRound className="h-3.5 w-3.5" />
                {student.supportKey ? "Support Key: Enabled" : "Enable Support Key"}
              </Button>
            </div>
          </div>
        </Card>

        {/* 4 Stat Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 w-full">
          {/* Card 1: Chat Groups */}
          <Card className="rounded-2xl border shadow-xs bg-card p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <MessagesSquare className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-muted-foreground truncate">Chat Groups</p>
                <p className="text-lg sm:text-xl font-black text-foreground">{student.chatGroups?.length || 0}</p>
              </div>
            </div>
          </Card>

          {/* Card 2: Purchases */}
          <Card className="rounded-2xl border shadow-xs bg-card p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <BookOpen className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-muted-foreground truncate">Chapters Unlocked</p>
                <p className="text-lg sm:text-xl font-black text-foreground">{student.purchases?.length || 0}</p>
              </div>
            </div>
          </Card>

          {/* Card 3: Account Status */}
          <Card className="rounded-2xl border shadow-xs bg-card p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "h-10 w-10 rounded-xl flex items-center justify-center shrink-0",
                student.status === "active" ? "bg-emerald-500/15 text-emerald-600" : "bg-destructive/15 text-destructive"
              )}>
                {student.status === "active" ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-muted-foreground truncate">Account</p>
                <p className="text-sm sm:text-base font-black text-foreground capitalize truncate">{student.status}</p>
              </div>
            </div>
          </Card>

          {/* Card 4: Support Key */}
          <Card className="rounded-2xl border shadow-xs bg-card p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "h-10 w-10 rounded-xl flex items-center justify-center shrink-0",
                student.supportKey ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-muted text-muted-foreground"
              )}>
                <KeyRound className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-muted-foreground truncate">Support Key</p>
                <p className="text-sm sm:text-base font-black text-foreground truncate">
                  {student.supportKey ? "Active" : "Disabled"}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* 2-Column Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
          {/* Left Column: Academic & Personal Details */}
          <div className="space-y-4 sm:space-y-6 lg:col-span-1 w-full min-w-0">
            <Card className="rounded-2xl border shadow-xs w-full">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                  <User className="h-4 w-4 text-primary shrink-0" /> Student Profile Info
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3.5 text-xs">
                <div>
                  <span className="text-muted-foreground block text-[11px]">Academic Cohort Year</span>
                  <span className="font-bold text-foreground text-sm">
                    {student.academicYear?.name || "Not Assigned"}
                  </span>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">College / Institute</span>
                  <div className="flex items-center gap-1.5 font-semibold text-foreground mt-0.5">
                    <School className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="break-words">{student.collegeName || "Not Provided"}</span>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">State / Region</span>
                  <div className="flex items-center gap-1.5 font-semibold text-foreground mt-0.5">
                    <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    <span>{student.state || "Not Provided"}</span>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">Referral Code</span>
                  <span className="font-mono font-bold text-foreground">
                    {student.referralCode || "None"}
                  </span>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">Enrollment Date</span>
                  <div className="flex items-center gap-1.5 font-semibold text-foreground mt-0.5">
                    <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span>
                      {student.createdAt
                        ? format(new Date(student.createdAt), "dd MMM yyyy, hh:mm a")
                        : "N/A"}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">Account ID (UUID)</span>
                  <div className="flex items-center justify-between gap-2 mt-1 p-2 rounded-xl bg-muted/40 border">
                    <span className="font-mono text-[10px] text-muted-foreground break-all truncate">
                      {student.id}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyId}
                      className="text-muted-foreground hover:text-foreground shrink-0 p-1"
                      title="Copy UUID"
                    >
                      {copiedId ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Chat Groups & Chapter Purchases */}
          <div className="space-y-4 sm:space-y-6 lg:col-span-2 w-full min-w-0">
            {/* Chat Groups Membership Card */}
            <Card className="rounded-2xl border shadow-xs w-full">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                    <MessagesSquare className="h-4 w-4 text-primary shrink-0" /> Active Chat Groups
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Cohorts and discussion rooms this student is currently participating in.
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="font-bold text-[10px] shrink-0">
                  {student.chatGroups?.length || 0} Groups
                </Badge>
              </CardHeader>
              <CardContent className="p-4">
                {!student.chatGroups || student.chatGroups.length === 0 ? (
                  <div className="p-8 text-center text-xs text-muted-foreground">
                    <MessagesSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    This student is not currently enrolled in any active chat groups.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {student.chatGroups.map((g) => (
                      <div
                        key={g.groupId}
                        onClick={() => navigate(`/chat-groups/${g.groupId}`)}
                        className="p-3.5 rounded-xl border bg-card hover:bg-muted/40 hover:border-primary/40 transition-all cursor-pointer flex flex-col justify-between space-y-2 group shadow-xs"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                              <MessagesSquare className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-xs text-foreground group-hover:text-primary transition-colors truncate">
                                {g.group?.name || "Group"}
                              </p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {g.group?.educationYear}
                              </p>
                            </div>
                          </div>
                          <Badge
                            variant={g.status === "active" ? "default" : "secondary"}
                            className="text-[9px] px-1.5 py-0 rounded shrink-0 uppercase font-bold"
                          >
                            {g.status}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1.5 border-t">
                          <span>
                            Joined: {g.joinedAt ? format(new Date(g.joinedAt), "dd MMM yyyy") : "N/A"}
                          </span>
                          <span className="flex items-center gap-1 text-primary font-bold">
                            Open Chat <ExternalLink className="h-2.5 w-2.5" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chapter Purchases / Lifetime Purchases Card */}
            <Card className="rounded-2xl border shadow-xs w-full">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                    <BookOpen className="h-4 w-4 text-emerald-500 shrink-0" /> Chapter-Wise Lifetime Purchases
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Chapters and learning content permanently unlocked by this student.
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="font-bold text-[10px] shrink-0">
                  {student.purchases?.length || 0} Chapters Unlocked
                </Badge>
              </CardHeader>
              <CardContent className="p-4">
                {!student.purchases || student.purchases.length === 0 ? (
                  <div className="p-8 text-center text-xs text-muted-foreground">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-30 text-emerald-500" />
                    No individual chapter purchases recorded yet for this student.
                  </div>
                ) : (
                  <div className="divide-y border rounded-xl overflow-hidden bg-card">
                    {student.purchases.map((p) => (
                      <div
                        key={p.id}
                        className="p-3 flex items-center justify-between gap-4 text-xs hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground truncate">
                              {p.chapter?.name || "Chapter Material"}
                            </p>
                            <p className="text-[10px] text-muted-foreground truncate">
                              Subject: {p.chapter?.subject?.name || "General Subject"}
                            </p>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <p className="font-bold text-foreground">
                            {p.amount !== undefined && p.amount !== null ? `₹${p.amount}` : "Active"}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {p.createdAt ? format(new Date(p.createdAt), "dd MMM yyyy") : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
